import {
  adminSlideSchema,
  CAROUSEL_MAX_SLIDES,
  mediaVariant,
  type AdminSlide,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateTimeInput,
  Field,
  Input,
  Notice,
  StatusBadge,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { isoToLisbonInput, lisbonInputToIso } from '../events/labels';
import { formatDateTime } from '../format';
import { IMAGE_ASPECTS, ImageField, useImagesBusy } from '../media/ImageUpload';

const BASE = '/api/site/content/carousels/inicio';

/** Carrossel da pagina inicial: slides por ordem, cada um com datas. */
export function CarouselView() {
  const [slides, setSlides] = useState<AdminSlide[] | null>(null);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getJson(BASE, z.array(adminSlideSchema))
      .then(setSlides)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const move = async (from: number, to: number) => {
    if (!slides) return;
    const ids = slides.map((s) => s.id);
    const [id] = ids.splice(from, 1);
    if (id === undefined) return;
    ids.splice(to, 0, id);
    setBusy(true);
    setError(null);
    try {
      setSlides(z.array(adminSlideSchema).parse(await send('PUT', `${BASE}/order`, { ids })));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slide: AdminSlide) => {
    if (!window.confirm(`Apagar o slide «${slide.title || slide.alt}»?`)) return;
    try {
      await send('DELETE', `/api/site/content/slides/${slide.id}`);
      load();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const live = slides?.filter((s) => s.live).length ?? 0;
  const current = slides?.find((s) => s.id === editing) ?? null;

  if (editing !== null) {
    return (
      <SlideForm
        key={editing}
        slide={current}
        onDone={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  return (
    <Card
      headingLevel={2}
      title="Carrossel da página inicial"
      actions={
        <Button size="sm" onClick={() => setEditing('new')}>
          Novo slide
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Os slides ligados e dentro das datas aparecem no topo de growup.gg, por esta ordem (no
          máximo {CAROUSEL_MAX_SLIDES}). Sem nenhum, o topo mostra o texto de sempre.{' '}
          {slides ? `${String(live)} no site agora.` : null}
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {slides && slides.length === 0 ? <p>Ainda não há slides.</p> : null}
        <ol className="flex flex-col gap-3">
          {slides?.map((s, i) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center gap-4 border-2 border-gu-line p-2"
            >
              <img
                src={mediaVariant(s.imageUrl, 'sm')}
                alt={s.alt}
                className="aspect-[16/7] w-48 bg-gu-sunken object-cover"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="font-semibold">{s.title || '(sem título)'}</span>
                <span className="text-caption text-gu-ink-muted">
                  {s.startsAt ? `De ${formatDateTime(s.startsAt)} ` : ''}
                  {s.endsAt ? `até ${formatDateTime(s.endsAt)}` : s.startsAt ? '' : 'Sem datas'}
                  {s.buttonLabel ? ` · Botão «${s.buttonLabel}» → ${s.buttonHref}` : ''}
                </span>
                <span>
                  <StatusBadge tone={s.live ? 'success' : 'neutral'}>
                    {s.live ? 'No site' : s.active ? 'Fora das datas' : 'Desligado'}
                  </StatusBadge>
                </span>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || i === 0}
                  aria-label={`Subir ${s.title || 'slide'}`}
                  onClick={() => void move(i, i - 1)}
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || i === slides.length - 1}
                  aria-label={`Descer ${s.title || 'slide'}`}
                  onClick={() => void move(i, i + 1)}
                >
                  ↓
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setEditing(s.id)}>
                  Editar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void remove(s)}>
                  Apagar
                </Button>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </Card>
  );
}

function SlideForm({ slide, onDone }: { slide: AdminSlide | null; onDone: () => void }) {
  const [imageUrl, setImageUrl] = useState<string | null>(slide?.imageUrl ?? null);
  const [mobileImageUrl, setMobileImageUrl] = useState<string | null>(
    slide?.mobileImageUrl ?? null,
  );
  const [alt, setAlt] = useState(slide?.alt ?? '');
  const [title, setTitle] = useState(slide?.title ?? '');
  const [text, setText] = useState(slide?.text ?? '');
  const [buttonLabel, setButtonLabel] = useState(slide?.buttonLabel ?? '');
  const [buttonHref, setButtonHref] = useState(slide?.buttonHref ?? '');
  const [active, setActive] = useState(slide?.active ?? true);
  const [startsAt, setStartsAt] = useState(isoToLisbonInput(slide?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(isoToLisbonInput(slide?.endsAt ?? null));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const photos = useImagesBusy();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (imageUrl === null) {
      setFields({ imageUrl: 'Escolha a imagem.' });
      return;
    }
    const body = {
      imageUrl,
      mobileImageUrl,
      alt,
      title,
      text,
      buttonLabel,
      buttonHref,
      active,
      startsAt: lisbonInputToIso(startsAt),
      endsAt: lisbonInputToIso(endsAt),
    };
    setBusy(true);
    setError(null);
    setFields({});
    try {
      if (slide)
        adminSlideSchema.parse(await send('PUT', `/api/site/content/slides/${slide.id}`, body));
      else adminSlideSchema.parse(await send('POST', BASE, body));
      onDone();
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title={slide ? 'Editar slide' : 'Novo slide'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <ImageField
            label="Imagem"
            value={imageUrl}
            onChange={setImageUrl}
            aspect={IMAGE_ASPECTS.carousel}
            onBusyChange={photos.track('imagem')}
            error={fields.imageUrl}
            hint="Larga (1920 × 840). O texto fica por cima, à esquerda: deixe esse lado mais limpo."
          />
          <ImageField
            label="Imagem para telemóvel"
            optional
            value={mobileImageUrl}
            onChange={setMobileImageUrl}
            aspect={IMAGE_ASPECTS.carouselMobile}
            onBusyChange={photos.track('telemovel')}
            error={fields.mobileImageUrl}
            hint="Mais alta (4:5). Sem ela, usa-se a outra, cortada ao centro."
          />
        </div>
        <Field label="Descrição da imagem" hint="Para quem não a vê." error={fields.alt}>
          <Input value={alt} maxLength={300} onChange={(e) => setAlt(e.target.value)} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Título" optional error={fields.title}>
            <Input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Texto" optional error={fields.text}>
            <Input value={text} maxLength={200} onChange={(e) => setText(e.target.value)} />
          </Field>
          <Field label="Botão: texto" optional error={fields.buttonLabel}>
            <Input
              value={buttonLabel}
              maxLength={30}
              onChange={(e) => setButtonLabel(e.target.value)}
            />
          </Field>
          <Field
            label="Botão: ligação"
            optional
            hint="Um caminho do site (/loja, /eventos/...) ou https://..."
            error={fields.buttonHref}
          >
            <Input value={buttonHref} onChange={(e) => setButtonHref(e.target.value)} />
          </Field>
          <Field
            label="Aparece a partir de"
            optional
            hint="Vazio = já. Hora de Lisboa."
            error={fields.startsAt}
          >
            <DateTimeInput value={startsAt} onChange={setStartsAt} />
          </Field>
          <Field label="Sai a" optional hint="Vazio = sem fim." error={fields.endsAt}>
            <DateTimeInput value={endsAt} onChange={setEndsAt} />
          </Field>
        </div>
        <Checkbox label="Ligado" checked={active} onChange={(e) => setActive(e.target.checked)} />
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="flex gap-2">
          <Button type="submit" disabled={busy || photos.busy}>
            Guardar
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
